import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarClock, CheckCircle2, Play, Plus, XCircle, LifeBuoy } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore, type MaintenanceJob, type MaintenanceStatus } from '../../../store/assetStore';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

const STATUS: Record<MaintenanceStatus, { label: string; tone: 'muted' | 'warning' | 'success' | 'destructive' }> = {
  scheduled: { label: 'Scheduled', tone: 'muted' },
  in_progress: { label: 'In progress', tone: 'warning' },
  completed: { label: 'Completed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'destructive' },
};

const SERVICE_TYPES = ['Preventive maintenance', 'Repair', 'Warranty claim', 'Upgrade', 'Inspection', 'Cleaning'];
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_FORM = { assetId: '', maintenance_type: SERVICE_TYPES[0], scheduled_date: today(), supplier_id: '', vendor: '', cost: '', description: '' };

/**
 * Service schedules — assets.asset_maintenance. Starting a job puts the asset
 * Under Maintenance; completing or cancelling it puts the asset back.
 */
export function ServiceScheduleView() {
  const { assets, maintenance, fetchAssets, fetchMaintenance, createMaintenance, updateMaintenance } = useAssetStore();
  const { vendors, fetchVendors } = useAssetCatalogStore();
  const [form, setForm] = useState(EMPTY_FORM);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchAssets();
    fetchMaintenance().finally(() => setLoaded(true));
    fetchVendors();
  }, [fetchAssets, fetchMaintenance, fetchVendors]);

  const serviceable = useMemo(() => assets.filter((a) => a.status !== 'Retired' && a.status !== 'Disposed'), [assets]);
  const rows = filter === 'open' ? maintenance.filter((m) => m.status === 'scheduled' || m.status === 'in_progress') : maintenance;

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.assetId) { toast.error('Choose the asset to service'); return; }
    setSaving(true);
    try {
      const res = await createMaintenance({
        assetId: form.assetId,
        maintenance_type: form.maintenance_type,
        scheduled_date: form.scheduled_date,
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        vendor: form.vendor || undefined,
        cost: form.cost === '' ? null : Number(form.cost),
        description: form.description || undefined,
      });

      const ticketRef = res?.ticketNo || res?.ticket_no || (res as any)?.ticketId || (res as any)?.ticket_id;
      if (ticketRef) {
        toast.success(`Service scheduled & ticket ${ticketRef} opened in Support Desk`);
      } else {
        toast.success('Service scheduled & ticket opened in Support Desk');
      }
      setForm(EMPTY_FORM);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const move = async (job: MaintenanceJob, status: MaintenanceStatus) => {
    setBusyId(job.id);
    try {
      await updateMaintenance(job.id, { status });
      try {
        const { useUnifiedSyncStore } = await import('../../../store/unifiedSyncStore');
        await useUnifiedSyncStore.getState().syncModuleActionToTicket({
          recordType: 'asset_fault',
          recordId: job.id,
          action: status,
          moduleStatus: status,
          note: `Service job marked ${status} in Assets > Service & Maintenance`,
        });
      } catch (err) {
        console.warn('Ticket sync fallback:', err);
      }
      toast.success(`${job.assetCode}: ${STATUS[status].label.toLowerCase()}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader className="mb-0" icon={CalendarClock} title="Service schedule" description="Plan and track maintenance, repairs and warranty claims." />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4">
          <h2 className="mb-3 font-display text-card-title text-foreground">Schedule a service</h2>
          <form className="space-y-3" onSubmit={create}>
            <select className={fieldClass} value={form.assetId} onChange={(e) => setForm({ ...form, assetId: e.target.value })}>
              <option value="">{serviceable.length ? 'Asset' : 'No assets registered'}</option>
              {serviceable.map((a) => <option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}
            </select>
            <select className={fieldClass} value={form.maintenance_type} onChange={(e) => setForm({ ...form, maintenance_type: e.target.value })}>
              {SERVICE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Scheduled for</span>
              <input className={fieldClass} type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
            </label>
            <select className={fieldClass} value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
              <option value="">Service vendor (optional)</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <input className={fieldClass} placeholder="Technician (optional)" value={form.vendor} maxLength={200}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
            <input className={fieldClass} type="number" min={0} step="0.01" placeholder="Estimated cost (optional)" value={form.cost}
              onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            <textarea
              className="min-h-16 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30"
              placeholder="What needs doing" value={form.description} maxLength={2000}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Button type="submit" className="w-full" isLoading={saving}><Plus /> Schedule</Button>
          </form>
        </Card>

        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
            <h2 className="flex items-center gap-2 font-display text-card-title text-foreground"><CalendarClock className="size-4" /> Service jobs ({rows.length})</h2>
            <div className="flex items-center gap-4 text-xs">
              {(['open', 'all'] as const).map((f) => (
                <button key={f} type="button" onClick={() => setFilter(f)}
                  className={`pb-1 font-medium transition-colors border-b-2 -mb-px cursor-pointer ${filter === f ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  {f === 'open' ? 'Open' : 'All'}
                </button>
              ))}
            </div>
          </div>
          {rows.length === 0 ? (
            <EmptyState className="m-4 border-0" title={loaded ? (filter === 'open' ? 'No open service jobs' : 'No service jobs yet') : 'Loading…'} description="Scheduled maintenance and repairs appear here." />
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-foreground">
                        {m.maintenanceType} · <span className="text-primary">{m.assetCode}</span> <span className="font-normal text-muted-foreground">{m.assetName}</span>
                      </p>
                      {(m.ticketNo || m.ticket_no || m.ticketId || m.ticket_id) && (
                        <a
                          href={`/support/tickets?ticketId=${encodeURIComponent(String(m.ticketNo || m.ticket_no || m.ticketId || m.ticket_id))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded bg-info/10 px-2 py-0.5 font-mono text-xs font-semibold text-info-strong border border-info/20 hover:bg-info/20 transition-colors"
                          title="Open linked Service Request ticket in Support Desk"
                        >
                          <LifeBuoy className="size-3 text-info" />
                          <span>Ticket: {m.ticketNo || m.ticket_no || m.ticketId || m.ticket_id}</span>
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {m.scheduledDate ? `Scheduled ${m.scheduledDate}` : 'No date'}
                      {m.completedDate ? ` · done ${m.completedDate}` : ''}
                      {m.supplierName || m.vendor ? ` · ${[m.supplierName, m.vendor].filter(Boolean).join(' / ')}` : ''}
                      {m.cost !== null && m.cost !== undefined ? ` · cost ${m.cost.toLocaleString()}` : ''}
                    </p>
                    {m.description && <p className="mt-0.5 text-xs text-muted-foreground">{m.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={STATUS[m.status].tone}>{STATUS[m.status].label}</Badge>
                    {m.status === 'scheduled' && (
                      <Button size="xs" variant="secondary" onClick={() => move(m, 'in_progress')} isLoading={busyId === m.id}><Play /> Start</Button>
                    )}
                    {m.status === 'in_progress' && (
                      <Button size="xs" onClick={() => move(m, 'completed')} isLoading={busyId === m.id}><CheckCircle2 /> Complete</Button>
                    )}
                    {(m.status === 'scheduled' || m.status === 'in_progress') && (
                      <Button size="xs" variant="ghost" onClick={() => move(m, 'cancelled')} disabled={busyId === m.id} aria-label="Cancel job"><XCircle /></Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
